import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  Award,
  Bell,
  Bookmark,
  Check,
  Coins,
  Gift,
  Heart,
  Loader2,
  MessageCircle,
  MessageSquare,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import type { Notification, NotificationType } from '@api/generated/types.gen';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
  useUnreadCount,
} from './use-notifications';

// Compact relative time (e.g. "3m", "2h", "5d") — avoids a date library dep.
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.max(0, Math.floor(diff / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  return `${wk}w ago`;
}

type Presenter = {
  icon: LucideIcon;
  iconClass: string;
  text: (n: Notification) => ReactNode;
};

const presenters: Record<NotificationType, Presenter> = {
  POST_LIKED: {
    icon: Heart,
    iconClass: 'text-pink-500 bg-pink-500/10',
    text: (n) => (
      <>
        <b>{n.actorUsername}</b> liked your post "{n.postTitle}"
      </>
    ),
  },
  POST_SAVED: {
    icon: Bookmark,
    iconClass: 'text-blue-500 bg-blue-500/10',
    text: (n) => (
      <>
        <b>{n.actorUsername}</b> saved your post "{n.postTitle}"
      </>
    ),
  },
  POST_COMMENTED: {
    icon: MessageCircle,
    iconClass: 'text-green-500 bg-green-500/10',
    text: (n) => (
      <>
        <b>{n.actorUsername}</b> commented on "{n.postTitle}"
      </>
    ),
  },
  COMMENT_REPLIED: {
    icon: MessageSquare,
    iconClass: 'text-purple-500 bg-purple-500/10',
    text: (n) => (
      <>
        <b>{n.actorUsername}</b> replied to your comment
      </>
    ),
  },
  COMBO_PINNED: {
    icon: Sparkles,
    iconClass: 'text-yellow-500 bg-yellow-500/10',
    text: (n) => (
      <>
        <b>{n.actorUsername}</b> pinned a combo from "{n.postTitle}"
      </>
    ),
  },
  REWARD_EARNED: {
    icon: Coins,
    iconClass: 'text-amber-500 bg-amber-500/10',
    text: (n) => {
      const parts: string[] = [];
      if (n.xpAmount) parts.push(`${n.xpAmount} XP`);
      if (n.tokenAmount) parts.push(`${n.tokenAmount} tokens`);
      return <>You earned {parts.join(' + ') || 'a reward'}</>;
    },
  },
  BADGE_EARNED: {
    icon: Award,
    iconClass: 'text-violet-500 bg-violet-500/10',
    text: (n) => <>You unlocked the "{n.rewardName}" badge</>,
  },
  BADGE_UPGRADED: {
    icon: Sparkles,
    iconClass: 'text-fuchsia-500 bg-fuchsia-500/10',
    text: (n) => <>Your "{n.rewardName}" badge evolved to a higher tier</>,
  },
  BOX_EARNED: {
    icon: Gift,
    iconClass: 'text-primary bg-primary/10',
    text: () => <>You earned a new pack — open it in the Overwolf app</>,
  },
};

function NotificationRow({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead: (id: string) => void;
}) {
  const p = presenters[notification.type];
  const Icon = p.icon;
  return (
    <button
      type="button"
      onClick={() => onRead(notification.id)}
      className={cn(
        'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50',
        !notification.isRead && 'bg-accent/30',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          p.iconClass,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-foreground">
          {p.text(notification)}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {timeAgo(notification.createdAt)}
        </span>
      </span>
      {!notification.isRead && (
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
      )}
    </button>
  );
}

export function NotificationsBell() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useNotifications(isAuthenticated);
  const { data: unread } = useUnreadCount(isAuthenticated);
  const markRead = useMarkNotificationAsRead();
  const markAll = useMarkAllNotificationsAsRead();

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Notifications are a signed-in-only feature.
  if (!isAuthenticated) return null;

  const notifications = data?.notifications ?? [];
  const unreadCount = unread?.count ?? 0;

  const handleRead = (id: string) => {
    markRead.mutate({ path: { id } });
  };

  const handleMarkAll = () => {
    markAll.mutate(
      {},
      { onError: () => toast.error('Failed to mark all as read') },
    );
  };

  // Self-positioned dropdown instead of a Radix popover: the lib/ui Popover's
  // floating-ui positioning mis-measures inside the sticky/blurred header and
  // renders the panel off-screen (the Overwolf panel worked around the same
  // bug with hardcoded coords). The bell is always top-right, so anchoring the
  // panel `fixed` under the header is reliable on mobile and desktop.
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-10 w-10"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <>
          {/* Click-away backdrop. */}
          <button
            type="button"
            aria-label="Close notifications"
            className="fixed inset-0 z-[90] cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Notifications"
            className="fixed right-2 top-[3.75rem] z-[100] w-[calc(100vw-1rem)] max-w-sm overflow-hidden rounded-xl border border-border/60 bg-popover text-popover-foreground shadow-lg sm:right-4"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleMarkAll}
                  disabled={markAll.isPending}
                >
                  <Check className="mr-1 h-3 w-3" />
                  Mark all read
                </Button>
              )}
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : isError ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                  Couldn't load notifications.
                </p>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center px-4 py-10 text-center">
                  <Bell className="mb-2 h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm font-medium">You're all caught up</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Rewards and activity will show up here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                  {notifications.map((n) => (
                    <NotificationRow
                      key={n.id}
                      notification={n}
                      onRead={handleRead}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
